// utils/bookingDateRange.js
//
// ONE answer to "does this booking fall inside the selected الفترة الزمنية",
// plus the Arabic vocabulary for the dropdown. Two surfaces render it:
//   - pages/BookingsList.js          (/bookings)
//   - components/SecurePropertyDetails.js  (property → bookings tab)
//
// WHY THIS FILE EXISTS: both surfaces carried a byte-identical copy of the same
// switch statement, so every new option had to be added twice and the two were
// free to drift. Same reasoning as utils/availabilityUiHelpers.js.
//
// WHY IT COMPARES CALENDAR-DAY STRINGS, not Date objects. `booking.checkIn` is a
// **business date** — a calendar day, no time-of-day — and its storage anchor is
// deliberately NOT uniform: OTA imports land on UTC midnight, other paths are
// Riyadh-anchored (docs/03_Technical/TEMPORAL_FIELD_CATALOGUE.md → Booking).
// The old code compared those raw instants against browser-local midnight, which
// silently dropped every check-in on the LAST day of a range: a check-in stored
// at UTC midnight reads as 03:00 Riyadh, and 03:00 on the 31st is > midnight on
// the 31st. Resolving each booking to its Riyadh calendar day first collapses
// both storage anchors onto the same day, and an inclusive YYYY-MM-DD string
// comparison has no boundary to get wrong.
//
// The window itself is derived from the Riyadh day, not the browser's — a browser
// behind Riyadh disagrees about which month it is for the first three hours of
// every month. Precedent: businessMonthKey in utils/dateValueHelper.js.
//
// SCOPE: this is a client-side refinement over an already-fetched list, matching
// how these two pages filter today. If the bookings payload ever grows past a
// page-load, the range belongs on the server (checkInFrom/checkInTo already exist
// on GET /api/v1/bookings) — see docs/03_Technical/API_DATA_FETCHING_PATTERNS.md.
//
// Arabic-only, per the Arabic-first mandate — neither surface is on the translated
// set (I18N_STRATEGY Phase 2, not shipped).

import { riyadhDayIso } from './dateValueHelper';

export const DATE_RANGE_ALL = 'all';
export const DATE_RANGE_CUSTOM = 'custom';

/** Dropdown vocabulary. Existing four kept in their original order; the two new
 *  ones are appended so the live dropdown does not reshuffle under ops. */
export const DATE_RANGE_OPTIONS = [
  { value: DATE_RANGE_ALL, label: 'كل الأوقات' },
  { value: 'thisWeek', label: 'هذا الأسبوع' },
  { value: 'thisMonth', label: 'هذا الشهر' },
  { value: 'nextMonth', label: 'الشهر القادم' },
  { value: 'lastMonth', label: 'الشهر الماضي' },
  { value: DATE_RANGE_CUSTOM, label: 'مخصص' }
];

const pad = (n) => String(n).padStart(2, '0');

/** 'YYYY-MM-DD' → a Date anchored at UTC midnight, used ONLY as a calculator.
 *  UTC arithmetic keeps the maths free of the host zone's offset. */
const parseDay = (iso) => {
  const [y, m, d] = String(iso).split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};

const formatDay = (d) => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;

const addDays = (iso, days) => {
  const d = parseDay(iso);
  d.setUTCDate(d.getUTCDate() + days);
  return formatDay(d);
};

/** Whole calendar month, `offset` months from the one `todayIso` sits in.
 *  Date.UTC normalises month overflow, so December → January and January → the
 *  previous December need no special case. */
const monthRange = (todayIso, offset) => {
  const d = parseDay(todayIso);
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth();
  return {
    from: formatDay(new Date(Date.UTC(y, m + offset, 1))),
    to: formatDay(new Date(Date.UTC(y, m + offset + 1, 0)))
  };
};

/**
 * The selected preset as an inclusive business-day window.
 *
 * @param {string} range - one of DATE_RANGE_OPTIONS[].value
 * @param {{from?: string, to?: string}} custom - the typed YYYY-MM-DD pair, for 'custom'
 * @returns {{from: string, to: string}|null} null means "do not filter" — used by
 *          'all', by an unknown key, and by a half-entered custom range (showing
 *          an empty table while the operator is still typing reads as a bug).
 */
export const resolveDateRange = (range, custom = {}) => {
  const today = riyadhDayIso(new Date());
  if (!today) return null;

  switch (range) {
    case 'thisWeek': {
      // Saudi week runs Sunday→Saturday; getUTCDay() is 0 on Sunday.
      const from = addDays(today, -parseDay(today).getUTCDay());
      return { from, to: addDays(from, 6) };
    }
    case 'thisMonth':
      return monthRange(today, 0);
    case 'lastMonth':
      return monthRange(today, -1);
    case 'nextMonth':
      return monthRange(today, 1);
    case DATE_RANGE_CUSTOM:
      return custom.from && custom.to ? { from: custom.from, to: custom.to } : null;
    default:
      return null;
  }
};

/**
 * Keep the bookings whose CHECK-IN day falls inside the selected window.
 *
 * Matches on check-in only — a stay spanning the boundary is filtered by the day
 * the guest arrives, which is how both surfaces have always behaved.
 *
 * @param {Array} bookings
 * @param {string} range
 * @param {{from?: string, to?: string}} custom
 * @returns {Array} the same array reference when no window applies
 */
export const filterBookingsByDateRange = (bookings, range, custom = {}) => {
  const window = resolveDateRange(range, custom);
  if (!window) return bookings;

  return bookings.filter(booking => {
    // `startDate` is the property-details shape of the same field.
    const day = riyadhDayIso(booking.checkIn || booking.startDate);
    return day !== '' && day >= window.from && day <= window.to;
  });
};
